<template>
  <div>

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">My Interviews</h5>
        <p class="page-sub">Interviews you are assigned to evaluate</p>
      </div>
    </div>

    <!-- No employee profile guard -->
    <div v-if="!employeeId && !loading" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Contact HR to link your account to an employee record.</p>
    </div>

    <template v-else>

      <!-- Skeleton -->
      <div v-if="loading" class="int-board">
        <div v-for="n in 6" :key="n" class="int-card sk-int-card">
          <div class="sk-line sk-line--wide"></div>
          <div class="sk-line"></div>
          <div class="sk-line sk-line--short"></div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!interviews.length" class="empty-state">
        <font-awesome-icon icon="calendar-check" class="empty-icon" />
        <p class="empty-title">No interviews assigned</p>
        <p class="empty-sub">You haven't been assigned to any interview panels yet.</p>
      </div>

      <!-- Board -->
      <div v-else class="int-board">
        <div
          v-for="s in interviews"
          :key="s.id"
          class="int-card"
          :class="{ 'int-card--cancelled': s.status === 'cancelled', 'int-card--completed': s.status === 'completed' }"
          @click="openDetail(s)"
        >
          <!-- Candidate avatar + name -->
          <div class="int-card-head">
            <div class="int-av-circle">{{ initials(s.application?.candidate) }}</div>
            <div class="int-card-info">
              <p class="int-card-name">{{ s.application?.candidate?.first_name }} {{ s.application?.candidate?.last_name }}</p>
              <p class="int-card-role">{{ s.application?.posting?.title || '—' }}</p>
            </div>
            <span class="chip" :class="statusClass(s.status)" style="margin-left:auto;flex-shrink:0">{{ s.status }}</span>
          </div>

          <!-- Stage + type badges -->
          <div class="int-card-badges">
            <span class="int-type-badge" :class="typeBadge(s.type)">{{ s.type }}</span>
            <span class="chip chip--blue" style="font-size:10px;padding:2px 8px">{{ stageLabel(s.stage) }}</span>
            <span v-if="myRating(s)?.submitted_at" class="chip chip--green" style="font-size:10px;padding:2px 8px">Rated</span>
          </div>

          <!-- Date/time -->
          <div class="int-dt-row">
            <font-awesome-icon icon="calendar-alt" class="int-dt-icon" />
            <span>{{ fmtDateTime(s.scheduled_at) }}</span>
          </div>

          <!-- Location/link -->
          <div v-if="s.location_or_link" class="int-dt-row">
            <font-awesome-icon icon="link" class="int-dt-icon" />
            <span class="int-link-text">{{ s.location_or_link }}</span>
          </div>

          <!-- All interviewers -->
          <div class="int-iv-row">
            <span v-for="iv in s.interviewers" :key="iv.id" class="int-avatar" :title="iv.user?.first_name + ' ' + iv.user?.last_name">
              {{ initials(iv.user) }}
            </span>
          </div>

          <!-- CTA -->
          <button
            v-if="s.status !== 'cancelled'"
            class="int-rate-btn"
            :class="myRating(s)?.submitted_at ? 'int-rate-btn--done' : 'int-rate-btn--cta'"
            @click.stop="openDetail(s)"
          >
            <font-awesome-icon :icon="myRating(s)?.submitted_at ? 'eye' : 'star'" />
            {{ myRating(s)?.submitted_at ? 'View My Evaluation' : 'Open Rating Sheet' }}
          </button>
        </div>
      </div>

    </template>

    <!-- ══════════════════════  RATING SHEET MODAL  ══════════════════════ -->
    <div class="modal-overlay" v-if="sheet.open" @click.self="sheet.open = false">
      <div class="modal-box modal-box--xl sheet-box">

        <!-- Modal header -->
        <div class="modal-head">
          <div>
            <p class="modal-title">Interview Rating Sheet</p>
            <p class="modal-sub" v-if="sheet.data">
              {{ sheet.data.interview?.application?.candidate?.first_name }}
              {{ sheet.data.interview?.application?.candidate?.last_name }}
              &rsaquo; {{ sheet.data.interview?.application?.posting?.title }}
            </p>
          </div>
          <button class="modal-close" @click="sheet.open = false"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Loading -->
        <div v-if="sheet.loading" class="modal-body" style="text-align:center;padding:40px">
          <font-awesome-icon icon="spinner" spin style="font-size:28px;color:#004AAD" />
        </div>

        <!-- Already rated banner -->
        <div v-if="sheet.data && sheet.data.my_rating?.submitted_at && !sheet.loading" class="rated-banner">
          <font-awesome-icon icon="check-circle" />
          You have already submitted an evaluation for this candidate. The form is read-only.
        </div>

        <!-- Two-column content -->
        <div v-if="sheet.data && !sheet.loading" class="sheet-body">

          <!-- ── LEFT: Criteria + Decision ── -->
          <div class="sheet-left">

            <!-- Criteria cards -->
            <template v-if="sheetCriteria.length">
              <div v-for="c in sheetCriteria" :key="c.id" class="criterion-card">
                <div class="criterion-head">
                  <div class="criterion-av">{{ initials2(c.name) }}</div>
                  <div>
                    <p class="criterion-name">{{ c.name }}</p>
                    <p class="criterion-desc">{{ c.description || 'No description' }}</p>
                  </div>
                  <span class="weight-chip">Weight: {{ c.weight }}%</span>
                </div>
                <div class="criterion-inputs">
                  <div class="criterion-score-wrap">
                    <label class="ci-label">Score (0–100)</label>
                    <input
                      type="number" min="0" max="100"
                      class="ci-input"
                      v-model.number="ratingForm.scores[c.id]"
                      :disabled="isAlreadyRated"
                      placeholder="0"
                    />
                  </div>
                  <div class="criterion-feedback-wrap">
                    <label class="ci-label">Specific Feedback</label>
                    <textarea
                      class="ci-input ci-textarea"
                      v-model="ratingForm.feedback[c.id]"
                      :disabled="isAlreadyRated"
                      placeholder="Describe the candidate's proficiency…"
                    ></textarea>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="no-criteria-note">
              No assessment criteria defined for this interview. You can still select a decision and add a summary below.
            </div>

            <!-- Decision + Summary -->
            <div class="decision-card">
              <p class="decision-title">Final Assessment Decision</p>

              <div class="decision-cards-row">
                <!-- Do Not Proceed -->
                <div
                  class="dec-opt dec-opt--reject"
                  :class="{ 'dec-opt--active': ratingForm.decision === 'do_not_proceed' }"
                  @click="!isAlreadyRated && (ratingForm.decision = 'do_not_proceed')"
                >
                  <font-awesome-icon icon="times-circle" class="dec-icon" />
                  <span>Do Not Proceed</span>
                </div>
                <!-- Recommend -->
                <div
                  class="dec-opt dec-opt--ok"
                  :class="{ 'dec-opt--active': ratingForm.decision === 'recommend' }"
                  @click="!isAlreadyRated && (ratingForm.decision = 'recommend')"
                >
                  <font-awesome-icon icon="check-circle" class="dec-icon" />
                  <span>Recommend</span>
                </div>
                <!-- Strong Recommend -->
                <div
                  class="dec-opt dec-opt--strong"
                  :class="{ 'dec-opt--active': ratingForm.decision === 'strong_recommend' }"
                  @click="!isAlreadyRated && (ratingForm.decision = 'strong_recommend')"
                >
                  <font-awesome-icon icon="star" class="dec-icon" />
                  <span>Strong Recommend</span>
                </div>
              </div>

              <div class="form-row" style="margin-top:14px">
                <label class="ci-label">Overall Summary <span class="req">*</span></label>
                <textarea
                  class="ci-input ci-textarea"
                  rows="4"
                  v-model="ratingForm.overall_comment"
                  :disabled="isAlreadyRated"
                  placeholder="Write a final summary of the candidate's performance…"
                ></textarea>
              </div>

              <div v-if="!isAlreadyRated" class="sheet-footer-actions">
                <button class="btn-outline" @click="sheet.open = false">Cancel</button>
                <button class="btn-primary" :disabled="submitting" @click="submitRating">
                  {{ submitting ? 'Submitting…' : 'Submit Evaluation' }}
                </button>
              </div>
            </div>
          </div>

          <!-- ── RIGHT: Candidate Info + Live Score ── -->
          <div class="sheet-right">

            <!-- Candidate card -->
            <div class="side-card">
              <div class="side-cand-head">
                <div class="side-av">{{ initials(sheet.data.interview?.application?.candidate) }}</div>
                <div>
                  <p class="side-cand-name">
                    {{ sheet.data.interview?.application?.candidate?.first_name }}
                    {{ sheet.data.interview?.application?.candidate?.last_name }}
                  </p>
                  <p class="side-cand-role">{{ sheet.data.interview?.application?.posting?.title }}</p>
                </div>
              </div>

              <div class="side-info-list">
                <div class="side-info-row">
                  <span class="side-info-label">INTERVIEW STAGE</span>
                  <span class="side-info-val">{{ stageLabel(sheet.data.interview?.stage) }}</span>
                </div>
                <div class="side-info-row">
                  <span class="side-info-label">SCHEDULED</span>
                  <span class="side-info-val">{{ fmtDateTime(sheet.data.interview?.scheduled_at) }}</span>
                </div>
                <div class="side-info-row">
                  <span class="side-info-label">TYPE</span>
                  <span class="side-info-val" style="text-transform:capitalize">{{ sheet.data.interview?.type }}</span>
                </div>
                <div class="side-info-row">
                  <span class="side-info-label">EMAIL</span>
                  <span class="side-info-val">{{ sheet.data.interview?.application?.candidate?.email || '—' }}</span>
                </div>
                <div class="side-info-row">
                  <span class="side-info-label">STATUS</span>
                  <span class="chip" :class="statusClass(sheet.data.interview?.status)" style="font-size:10px">{{ sheet.data.interview?.status }}</span>
                </div>
              </div>
            </div>

            <!-- Live score card -->
            <div class="side-card" v-if="sheetCriteria.length">
              <p class="side-score-title">CURRENT EVALUATION</p>
              <div class="score-circle-wrap">
                <svg class="score-circle-svg" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#eef2f7" stroke-width="10"/>
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#004AAD" stroke-width="10"
                    stroke-linecap="round"
                    :stroke-dasharray="`${overallPct * 3.14159} 314.159`"
                    stroke-dashoffset="78.54"
                    transform="rotate(-90 60 60)"
                  />
                  <text x="60" y="65" text-anchor="middle" font-size="22" font-weight="700" fill="#004AAD">{{ Math.round(overallPct) }}%</text>
                </svg>
              </div>
              <div class="score-breakdown">
                <div v-for="c in sheetCriteria" :key="c.id" class="score-row">
                  <span class="score-row-label">{{ c.name }} ({{ c.weight }}%)</span>
                  <span class="score-row-val">{{ ratingForm.scores[c.id] ?? '—' }}</span>
                </div>
              </div>
            </div>

            <!-- Interviewer guidance -->
            <div class="side-card side-card--blue">
              <p class="guidance-title">INTERVIEWER GUIDANCE</p>
              <ul class="guidance-list">
                <li>Evaluate based on demonstrated skills and clear examples.</li>
                <li>Score each criterion independently before making an overall decision.</li>
                <li>Be specific in your feedback — avoid vague assessments.</li>
                <li>Your rating is private and only visible to the recruitment team.</li>
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyInterviewsPage',

  data() {
    return {
      loading: false,
      interviews: [],

      sheet: { open: false, loading: false, data: null },
      submitting: false,
      ratingForm: { scores: {}, feedback: {}, decision: '', overall_comment: '' },
    };
  },

  computed: {
    employeeId() {
      return this.$store?.state?.getLoggedInUser?.employee_id || null;
    },
    isAlreadyRated() {
      return !!this.sheet.data?.my_rating?.submitted_at;
    },
    sheetCriteria() {
      const tpl = this.sheet.data?.interview?.assessment_template;
      if (tpl?.criteria?.length) return tpl.criteria;
      const comp = this.sheet.data?.interview?.competency_criteria;
      if (comp?.length) return comp.map((c, i) => ({ id: i, name: c.name || c, description: '', weight: Math.floor(100 / comp.length) }));
      return [];
    },
    overallPct() {
      const entries = Object.values(this.ratingForm.scores).filter(v => v !== null && v !== '' && !isNaN(v));
      if (!entries.length || !this.sheetCriteria.length) return 0;
      const total = entries.reduce((s, v) => s + Number(v), 0);
      return (total / (this.sheetCriteria.length * 100)) * 100;
    },
  },

  mounted() {
    this.fetchInterviews();
  },

  methods: {
    async fetchInterviews() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/interviews');
        this.interviews = data.data || [];
      } catch {
        this.$toast?.error('Failed to load interviews');
      } finally {
        this.loading = false;
      }
    },

    async openDetail(s) {
      this.sheet = { open: true, loading: true, data: null };
      this.ratingForm = { scores: {}, feedback: {}, decision: '', overall_comment: '' };
      try {
        const { data } = await axios.get(`/hr/self-service/interviews/${s.id}`);
        this.sheet.data = data.data;

        // Pre-fill if already rated
        const rated = data.data.my_rating;
        if (rated?.submitted_at) {
          this.ratingForm.decision       = rated.decision || '';
          this.ratingForm.overall_comment = rated.overall_comment || '';
          if (rated.scores) {
            Object.entries(rated.scores).forEach(([k, v]) => {
              this.ratingForm.scores[k] = v?.score ?? v;
              this.ratingForm.feedback[k] = v?.comment ?? '';
            });
          }
        }
      } catch {
        this.$toast?.error('Failed to load interview details');
        this.sheet.open = false;
      } finally {
        this.sheet.loading = false;
      }
    },

    async submitRating() {
      if (!this.ratingForm.decision) { this.$toast?.error('Please select a decision'); return; }
      if (!this.ratingForm.overall_comment?.trim()) { this.$toast?.error('Overall summary is required'); return; }
      this.submitting = true;
      try {
        // Build scores payload: { criterion_id: { score, comment } }
        const scores = {};
        this.sheetCriteria.forEach(c => {
          scores[c.id] = {
            score:   this.ratingForm.scores[c.id] ?? null,
            comment: this.ratingForm.feedback[c.id] ?? '',
          };
        });

        await axios.post(`/hr/self-service/interviews/${this.sheet.data.interview.id}/rating`, {
          scores:          scores,
          decision:        this.ratingForm.decision,
          overall_comment: this.ratingForm.overall_comment,
        });

        this.$toast?.success('Evaluation submitted successfully!');
        this.sheet.open = false;
        this.fetchInterviews();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Submission failed');
      } finally {
        this.submitting = false;
      }
    },

    myRating(s) {
      return s.ratings?.[0] || null;
    },

    initials(person) {
      if (!person) return '?';
      return ((person.first_name?.[0] || '') + (person.last_name?.[0] || '')).toUpperCase() || '?';
    },
    initials2(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },

    fmtDateTime(val) {
      if (!val) return '—';
      return new Date(val).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    stageLabel(s) {
      const map = { first: '1st Interview', second: '2nd Interview', third: '3rd Interview', final: 'Final', technical: 'Technical', hr: 'HR Round' };
      return map[s] || s || '—';
    },

    statusClass(s) {
      return { scheduled: 'chip--blue', rescheduled: 'chip--orange', completed: 'chip--green', cancelled: 'chip--red' }[s] || 'chip--gray';
    },

    typeBadge(t) {
      return { virtual: 'int-type-badge--virtual', physical: 'int-type-badge--physical', phone: 'int-type-badge--phone' }[t] || '';
    },
  },
};
</script>

<style scoped>
/* ── Board ── */
.int-board { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
.int-card { background:#fff; border:1px solid #e2e8f3; border-radius:12px; padding:18px; cursor:pointer; transition:box-shadow .15s,border-color .15s; }
.int-card:hover { box-shadow:0 4px 18px rgba(0,74,173,.1); border-color:#b8cef0; }
.int-card--cancelled { opacity:.7; border-color:#fecaca; background:#fff8f8; }
.int-card--completed { border-color:#bbf7d0; }

.int-card-head { display:flex; align-items:flex-start; gap:10px; margin-bottom:10px; }
.int-av-circle { width:38px; height:38px; border-radius:50%; background:#e0ecff; color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.int-card-info { flex:1; min-width:0; }
.int-card-name { font-weight:700; font-size:14px; color:#1d2b4f; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.int-card-role { font-size:11px; color:#7a90b0; margin:0; }

.int-card-badges { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:10px; }
.int-type-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; text-transform:uppercase; }
.int-type-badge--virtual  { background:#e8f0fe; color:#004AAD; }
.int-type-badge--physical { background:#fef3c7; color:#92400e; }
.int-type-badge--phone    { background:#f0fdf4; color:#166534; }

.int-dt-row { display:flex; align-items:center; gap:6px; font-size:12px; color:#4a5568; margin-bottom:6px; }
.int-dt-icon { color:#7a90b0; width:12px; flex-shrink:0; }
.int-link-text { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.int-iv-row { display:flex; gap:4px; flex-wrap:wrap; margin:10px 0 12px; }
.int-avatar { width:28px; height:28px; border-radius:50%; background:#c7d7f8; color:#1d4ed8; display:inline-flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; }

.int-rate-btn { width:100%; padding:8px; border-radius:8px; border:none; font-size:12px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; transition:background .15s; }
.int-rate-btn--cta  { background:#004AAD; color:#fff; }
.int-rate-btn--cta:hover { background:#003a8c; }
.int-rate-btn--done { background:#f0fdf4; color:#166534; border:1px solid #bbf7d0; }

/* ── Skeletons ── */
.sk-int-card { background:#f7f9fc; border-color:#eef2f7; cursor:default; }
.sk-line { height:12px; background:#e2e8f3; border-radius:6px; margin-bottom:10px; }
.sk-line--wide { width:80%; }
.sk-line--short { width:50%; }

/* ── Empty / no profile ── */
.empty-state,.no-profile-box { text-align:center; padding:60px 20px; }
.empty-icon,.no-profile-icon { font-size:48px; color:#c7d7f8; margin-bottom:16px; display:block; }
.empty-title,.no-profile-title { font-size:16px; font-weight:700; color:#1d2b4f; margin-bottom:6px; }
.empty-sub,.no-profile-sub { font-size:13px; color:#7a90b0; }

/* ── Rating Sheet Modal ── */
.sheet-box { max-width:1000px !important; }
.modal-head  { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:18px 24px; border-bottom:1px solid #e2e8f3; flex-shrink:0; }
.modal-title { font-size:16px; font-weight:700; color:#1d2b4f; margin:0; }
.modal-close { width:32px; height:32px; border-radius:8px; border:1px solid #dde5f0; background:#fff; color:#7a90b0; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; transition:background .15s,color .15s; }
.modal-close:hover { background:#f4f7fb; color:#1d2b4f; }
.modal-sub { font-size:12px; color:#7a90b0; margin:2px 0 0; }
.rated-banner { background:#f0fdf4; border-bottom:1px solid #bbf7d0; padding:10px 24px; font-size:13px; color:#166534; font-weight:600; display:flex; align-items:center; gap:8px; }

.sheet-body { display:grid; grid-template-columns:1fr 320px; gap:20px; padding:20px 24px; overflow-y:auto; max-height:76vh; }
@media (max-width:760px) { .sheet-body { grid-template-columns:1fr; } }

/* Criteria card */
.criterion-card { background:#f7f9fc; border:1px solid #e2e8f3; border-radius:10px; padding:18px; margin-bottom:14px; }
.criterion-head { display:flex; align-items:flex-start; gap:10px; margin-bottom:14px; }
.criterion-av { width:40px; height:40px; border-radius:6px; background:#e0ecff; color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.criterion-name { font-size:15px; font-weight:600; color:#1d2b4f; margin:0; }
.criterion-desc { font-size:12px; color:#7a90b0; margin:2px 0 0; }
.weight-chip { background:#e0ecff; color:#7a90b0; font-size:11px; font-weight:600; padding:3px 10px; border-radius:12px; white-space:nowrap; margin-left:auto; flex-shrink:0; }

.criterion-inputs { display:grid; grid-template-columns:0.4fr 1fr; gap:12px; }
@media (max-width:560px) { .criterion-inputs { grid-template-columns:1fr; } }
.criterion-score-wrap,.criterion-feedback-wrap { display:flex; flex-direction:column; gap:4px; }
.ci-label { font-size:12px; font-weight:600; color:#5a738a; }
.ci-input { border:1px solid #d4dce8; border-radius:6px; padding:8px 10px; font-size:13px; width:100%; background:#fff; color:#1d2b4f; }
.ci-input:disabled { opacity:.65; background:#f0f4f8; }
.ci-textarea { resize:none; min-height:66px; max-height:66px; }

.no-criteria-note { background:#fef9ec; border:1px solid #fcd34d; border-radius:8px; padding:12px 16px; font-size:13px; color:#92400e; margin-bottom:14px; }

/* Decision card */
.decision-card { background:#fff; border:1px solid #e2e8f3; border-radius:10px; padding:20px; margin-bottom:14px; }
.decision-title { font-size:16px; font-weight:700; color:#1d2b4f; margin:0 0 14px; }

.decision-cards-row { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:16px; }
@media (max-width:480px) { .decision-cards-row { grid-template-columns:1fr; } }

.dec-opt { border:1.5px solid #d4dce8; border-radius:10px; padding:14px 10px; text-align:center; cursor:pointer; transition:all .15s; display:flex; flex-direction:column; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#5a738a; }
.dec-opt:hover { border-color:#b0c4de; }
.dec-icon { font-size:22px; }

.dec-opt--reject.dec-opt--active   { border-color:#ef4444; background:#fef2f2; color:#ef4444; }
.dec-opt--ok.dec-opt--active       { border-color:#006d43; background:#f0fdf4; color:#006d43; }
.dec-opt--strong.dec-opt--active   { border-color:#ca8a04; background:#fefce8; color:#ca8a04; }
.dec-opt--reject .dec-icon  { color:#ef4444; }
.dec-opt--ok .dec-icon      { color:#006d43; }
.dec-opt--strong .dec-icon  { color:#ca8a04; }

.sheet-footer-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:16px; }

/* Side cards */
.sheet-right { display:flex; flex-direction:column; gap:14px; }
.side-card { background:#fff; border:1px solid #e2e8f3; border-radius:10px; padding:18px; }
.side-card--blue { background:#eff4ff; border-color:#c7d7f8; }

.side-cand-head { display:flex; gap:12px; align-items:flex-start; margin-bottom:16px; }
.side-av { width:48px; height:48px; border-radius:8px; background:#e0ecff; color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; flex-shrink:0; }
.side-cand-name { font-size:16px; font-weight:700; color:#1d2b4f; margin:0; }
.side-cand-role { font-size:12px; color:#7a90b0; margin:2px 0 0; }

.side-info-list { display:flex; flex-direction:column; gap:8px; }
.side-info-row { display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #eef2f7; padding-bottom:7px; }
.side-info-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.side-info-val { font-size:12px; font-weight:600; color:#1d2b4f; }

/* Score circle */
.side-score-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#1d2b4f; margin:0 0 12px; }
.score-circle-wrap { display:flex; justify-content:center; margin-bottom:14px; }
.score-circle-svg { width:100px; height:100px; }
.score-breakdown { display:flex; flex-direction:column; gap:6px; }
.score-row { display:flex; justify-content:space-between; font-size:12px; border-bottom:1px solid #eef2f7; padding-bottom:5px; }
.score-row-label { color:#5a738a; }
.score-row-val { font-weight:700; color:#1d2b4f; }

/* Guidance */
.guidance-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#004AAD; margin:0 0 10px; }
.guidance-list { margin:0; padding-left:16px; display:flex; flex-direction:column; gap:6px; }
.guidance-list li { font-size:12px; color:#3d4d6a; }

/* Chips */
.chip { display:inline-flex; align-items:center; padding:2px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--blue   { background:#e0ecff; color:#004AAD; }
.chip--green  { background:#dcfce7; color:#166534; }
.chip--orange { background:#fef3c7; color:#92400e; }
.chip--red    { background:#fee2e2; color:#b91c1c; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.req { color:#ef4444; margin-left:2px; }

/* Page header */
.page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:20px; }
.page-title { font-size:20px; font-weight:700; color:#1d2b4f; margin:0; }
.page-sub   { font-size:13px; color:#7a90b0; margin:2px 0 0; }

/* Buttons */
.btn-primary { background:#004AAD; color:#fff; border:none; border-radius:8px; padding:9px 20px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { background:#fff; color:#004AAD; border:1.5px solid #004AAD; border-radius:8px; padding:8px 18px; font-size:13px; font-weight:600; cursor:pointer; }
</style>
